import { Component, computed, effect, inject, input } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { AccessRevokedNoticeService } from '../../access-revoked-notice.service';
import * as fromLayout from '../../layout';
import { IntroductionComponent } from '../components/introduction.component';
import { TopBarMobileResponsive } from '../components/top-bar.mobile.responsive';
import * as fromShared from '../shared';
import { teammatesFeature, TeammatesState } from './store';
import { TeammateAssistantComponent } from './teammate-assistant.component';
import { getTeammateAvatarFromParts } from './teammate-avatar.config';

@Component({
  imports: [
    TopBarMobileResponsive,
    TranslateModule,
    TeammateAssistantComponent,
    IntroductionComponent,
  ],
  template: `
    <div class="bg-gradient-e-vertical h-svh w-full flex flex-col">
      @if (isMobile()) {
        <squadbox-top-bar-mobile
          class="bg-shades-white"
          (chevronLeftButtonClicked)="handleChevronLeftButtonCTA()"
        >
        </squadbox-top-bar-mobile>
      }

      @let teammate = selectedTeammate();
      <!--
        We want the scrollbar to be flush with the right side of the screen, not flush with the right side of the content.
        If the size of the viewport is > 1320px, this one-element div ensures, that the scrollbar is positioned correctly
      -->
      <div class="w-full flex flex-col overflow-y-auto fade-in-up-subtle">
        @if (teammate) {
          <div
            class="
            flex flex-col gap-6
            w-full max-w-370 self-center
            tablet-portrait:px-20 px-6
            tablet-portrait:pt-22 pt-6
            pb-6
          "
          >
            <squadbox-introduction
              [teammate]="true"
              [avatar]="getTeammateAvatar(teammate)"
              [title]="teammate.welcomeMessage"
              [subtitle]="teammate.purposeMessage"
            />

            <!-- Teammate Assistants -->
            <div
              class="
              grid gap-2 tablet-portrait:gap-4
              grid-cols-3 max-[1194px]:grid-cols-2 max-[650px]:grid-cols-1
            "
            >
              @for (assistant of teammate.assistants; track assistant.uuid) {
                <squadbox-teammate-assistant-card
                  class="group"
                  [cardData]="assistant"
                />
              }
            </div>
          </div>
        }
      </div>
    </div>
  `,
})
export class TeammatePage {
  private readonly layoutStore = inject(Store<fromLayout.LayoutState>);
  private readonly teammatesStore = inject<Store<TeammatesState>>(Store);
  private readonly accessRevokedNotice = inject(AccessRevokedNoticeService);
  private readonly routerService = inject(fromShared.RouterService);
  private readonly mobileNavigation = inject(
    fromShared.MobileNavigationService,
  );

  public readonly slug = input.required<string>();

  /** Resolve a teammate's avatar from its color/shape/eyes axes (template helper). */
  public readonly getTeammateAvatar = getTeammateAvatarFromParts;

  private readonly areTeammatesLoaded = this.teammatesStore.selectSignal(
    teammatesFeature.selectLoaded,
  );

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  public readonly selectedTeammate = computed(() => {
    const slug = this.slug();

    return slug
      ? this.teammatesStore.selectSignal(
          teammatesFeature.selectTeammateBySlug(slug),
        )()
      : undefined;
  });

  constructor() {
    // Remember the slug we've actually resolved a teammate for, so a live
    // revocation (it was visible, now it's gone) is distinguishable from a
    // never-accessible slug (a typo or stale link that was never theirs).
    let resolvedSlug: string | null = null;

    effect(() => {
      const slug = this.slug();
      if (!slug) return;

      const loaded = this.areTeammatesLoaded();
      if (!loaded) return;

      const teammate = this.selectedTeammate();
      if (teammate) {
        resolvedSlug = slug;
        return;
      }

      // Absent + loaded: if we had resolved this exact slug, the teammate was
      // revoked out from under the user → landing + "access removed" toast.
      // Otherwise it was never theirs → a plain 404, as before.
      if (resolvedSlug === slug) {
        void this.accessRevokedNotice.notify('TOAST.ACCESS_REVOKED.TEAMMATE');
      } else {
        void this.routerService.navigateToNotFound();
      }
    });
  }

  public async handleChevronLeftButtonCTA(): Promise<void> {
    await this.mobileNavigation.open();
  }
}
